<template>
  <div class="payment-methods-view">
    <SideBar
      :name="user?.name"
      :email="user?.email"
    />

    <main class="content-container container py-4">
      <div
        v-if="loading"
        class="loading-state py-5 text-center"
      >
        <div
          class="spinner-border text-energy-blue"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div
        v-else-if="user"
        class="fade-in"
      >
        <!-- List/Manage View -->
        <div v-if="!isAddingNew">
          <div class="mb-3">
            <BaseButton
              bare
              variant=""
              class="btn-back"
              @click="goBack"
            >
              <font-awesome-icon
                icon="arrow-left"
                class="me-1"
              />
              <span>Voltar</span>
            </BaseButton>
          </div>

          <div class="management-card card shadow-sm border-0">
            <div class="card-header bg-white border-0 pt-4 pb-0 text-center">
              <div class="icon-circle mb-3 mx-auto">
                <font-awesome-icon
                  icon="credit-card"
                  class="text-energy-blue"
                />
              </div>
              <h2 class="h5 fw-bold text-dark">
                {{ $t('manage_payment.title') }}
              </h2>
              <p class="text-muted small px-3">
                {{ $t('manage_payment.subtitle') }}
              </p>
            </div>

            <div class="card-body px-4 pb-4">
              <div
                v-if="user && !hasPixIdentification && (!user.credit_cards || user.credit_cards.length === 0)"
                class="empty-state text-center py-4 mb-3"
              >
                <font-awesome-icon
                  icon="info-circle"
                  class="text-muted mb-2"
                  size="2x"
                />
                <p class="text-muted small">
                  {{ $t('manage_payment.empty_state') }}
                </p>
              </div>

              <PaymentMethodSelector
                :user="user"
                :disable-delete="disableDelete"
                @user-updated="handleUserUpdate"
                @payment-method-added="handlePaymentAdded"
              />
            </div>
          </div>
        </div>

        <!-- Add New View (Keep for backward compatibility if needed, but Selector handles its own forms now) -->
        <div
          v-else
          class="selection-card card shadow-sm border-0"
        >
          <div class="card-header bg-white border-0 pt-4 pb-2">
            <div class="d-flex align-items-center">
              <BaseButton
                bare
                variant=""
                class="btn-back text-decoration-none"
                @click="isAddingNew = false"
              >
                <font-awesome-icon icon="arrow-left" />
              </BaseButton>
              <h4 class="h5 fw-bold m-0 flex-grow-1 text-center pe-5">
                {{ $t('select_payment_method.title') }}
              </h4>
            </div>
          </div>

          <div class="card-body px-4 pb-4">
            <PaymentMethodSelector
              :user="user"
              :disable-delete="disableDelete"
              @payment-method-added="handlePaymentAdded"
              @cancel="isAddingNew = false"
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
  import SideBar from '@/components/SideBar.vue'
  import PaymentMethodSelector from '@/components/MapNavigation/ui/PaymentMethodSelector.vue'
  import getUser from '@/views/home/service/getUser.js'
  import getLastTransaction from '@/views/home/service/getLastTransaction.js'
  import { TAKE_BATTERY_SUCCESS } from '@/constants.js'
  import BaseButton from '@/components/ui/BaseButton.vue'

  export default {
    name: 'PaymentMethods',
    components: {
      SideBar,
      PaymentMethodSelector,
      BaseButton
    },
    data() {
      return {
        user: null,
        loading: true,
        isAddingNew: false,
        lastTransactionStatus: null
      }
    },
    computed: {
      hasPixIdentification() {
        return Boolean(
          this.user?.pix_identification?.name &&
            this.user?.pix_identification?.cpf_or_cnpj
        )
      },
      disableDelete() {
        return this.lastTransactionStatus === TAKE_BATTERY_SUCCESS
      }
    },
    async mounted() {
      await this.fetchUser()
    },
    methods: {
      async fetchUser() {
        this.loading = true
        try {
          const [userData, lastTransaction] = await Promise.all([getUser(), getLastTransaction()])

          if (userData) {
            this.user = userData
          }

          if (lastTransaction) {
            this.lastTransactionStatus = lastTransaction.status
          }
        } catch (error) {
          console.error('Error fetching data:', error)
        } finally {
          this.loading = false
        }
      },
      handleUserUpdate(updatedUser) {
        this.user = updatedUser
      },
      async handlePaymentAdded() {
        await this.fetchUser()
        this.isAddingNew = false
      },
      goBack() {
        this.$router.push({ name: 'home_page' })
      }
    }
  }
</script>

<style scoped lang="scss">
  $color-energy-blue: #016ab3;
  $color-energy-orange: #f58a48;

  .payment-methods-view {
    min-height: 100vh;
    min-height: 100dvh;
    background-color: #f4f7f9;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* Smooth momentum scrolling on iOS */

    .content-container {
      max-width: 724px;
      padding-bottom: 80px; /* Extra space for keyboard and safe areas */
    }
  }

  .card {
    border-radius: 16px;
    overflow: hidden;
  }

  .icon-circle {
    width: 56px;
    height: 56px;
    background-color: rgba(1, 106, 179, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  .text-energy-blue {
    color: $color-energy-blue;
  }

  .btn-back {
    color: #6c757d;
    font-size: 0.9rem;
    font-weight: 600;
    padding: 0.5rem 1rem;
    border-radius: 50px;
    background: white;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: all 0.2s ease;
    &:hover {
      color: $color-energy-blue;
      border-color: $color-energy-blue;
      box-shadow: 0 2px 8px rgba(1, 106, 179, 0.12);
    }
  }

  .fade-in {
    animation: fadeIn 0.4s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  // Override component styles for better integration
  :deep(.manage-payment) {
    .page-title,
    .page-text {
      display: none; // Hidden because we use our own card header
    }

    .flex-row {
      background: #fff;
      border: 1px solid #edf2f7;
      border-radius: 12px;
      margin-bottom: 12px !important;
      padding: 12px;
      transition: all 0.2s ease;

      &.selected {
        border-color: $color-energy-orange;
        background-color: rgba(245, 138, 72, 0.02);
      }
    }

    .button.variant-secondary {
      width: 100%;
      border-radius: 10px;
      padding: 12px;
      font-weight: 600;
      margin: 10px 0 !important;
    }

    .button.variant-primary {
      width: 100%;
      border-radius: 10px;
      padding: 12px;
      font-weight: 600;
      margin: 5px 0 !important;
    }
  }
</style>
